import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useState } from 'react'

import {
  PartnerSummaryBlock,
  type PartnerSummaryBlockProps,
} from 'clickNgo/entities/partner/ui/partnerSummaryBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import {
  type AcknowledgmentSignerPickerIntent,
  type AcknowledgmentSignerSelectionPayload,
} from 'partner360/features/acknowledgmentSignerPicker'
import { isPlanReviewBeforeSign, PlanReviewDialog } from 'partner360/features/planReview'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { getBlockedSignOffReason } from '../lib/getBlockedSignOffReason'
import { isAcknowledgmentGroupComplete } from '../lib/isAcknowledgmentGroupComplete'
import { AcknowledgmentAction, CommitmentTypes } from '../model/enums'
import type {
  AcknowledgmentCommitmentItem,
  AcknowledgmentOffering,
  AcknowledgmentUpdatedPayload,
} from '../model/types'
import { useAcknowledgmentSimulation } from '../model/useAcknowledgmentSimulation'
import { useAcknowledgmentStatusDialog } from '../model/useAcknowledgmentStatusDialog'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import {
  type AcknowledgmentBlockedAction,
  AcknowledgmentBlockedBanner,
} from './AcknowledgmentBlockedBanner'
import { AcknowledgmentCommentDialog } from './AcknowledgmentCommentDialog'
import { AcknowledgmentDeleteDialog } from './AcknowledgmentDeleteDialog'
import { AcknowledgmentFooter } from './AcknowledgmentFooter'
import { AcknowledgmentPlanChangesDialog } from './AcknowledgmentPlanChangesDialog'
import { AcknowledgmentPrimaryBanner } from './AcknowledgmentPrimaryBanner'
import { AcknowledgmentTypeColumn } from './AcknowledgmentTypeColumn'
import { AcknowledgmentSignerActionDialog } from './AddPartnerDialog'

import styles from './AcknowledgmentStatusPanel.module.scss'

type PendingSignerActionState = {
  commitmentType: CommitmentTypes
  intent: AcknowledgmentSignerPickerIntent
  item?: AcknowledgmentCommitmentItem | null
  /** A SuperAdmin's PBM added through the Manager form; the column's name goes in the title. */
  byEmail?: { typeName: string }
}

type PendingCommentActionState = {
  mode: 'drop' | 'refuse'
  item: AcknowledgmentCommitmentItem
}

type PendingSignatureState = {
  mode: 'sign' | 'refuse'
  item: AcknowledgmentCommitmentItem
}

export type AcknowledgmentStatusPanelProps = {
  partnerId: string
  fYear: number
  /** `null`: a distributor's acknowledgment, one for every offering, and its plan reviewed across
   *  all of them. */
  offering: AcknowledgmentOffering
  /** The footer's close button; hosts that carry their own way out leave it off. */
  onClose?: () => void
  onSigned?: (item: AcknowledgmentCommitmentItem) => void
  onNotified?: (item: AcknowledgmentCommitmentItem) => void
  onUpdated?: (payload: AcknowledgmentUpdatedPayload) => void | Promise<void>
  partnerSummary?: PartnerSummaryBlockProps
  /** Off where the host already names the partner, e.g. the single-partner cockpit. */
  showPartnerSummary?: boolean
  /** Gives each signer column its type as an id, so a link can scroll to it. Only where the panel
   *  is the one on the page. */
  anchorColumns?: boolean
  /** Where a host can take the reader to what holds the signatures, e.g. the next incomplete
   *  offering of the cockpit. Without it the reason is still said. */
  blockedAction?: AcknowledgmentBlockedAction | null
  className?: string
}

/**
 * The acknowledgment status of one partner's offering: the signer columns, the primary action and
 * the actions each row offers. It carries no chrome of its own, so it stands both inside
 * `AcknowledgmentStatusDialog` and embedded in a host section.
 */
export const AcknowledgmentStatusPanel = ({
  onClose,
  partnerId,
  fYear,
  offering,
  onSigned,
  onNotified,
  onUpdated,
  partnerSummary,
  showPartnerSummary = true,
  anchorColumns = false,
  blockedAction,
  className,
}: AcknowledgmentStatusPanelProps) => {
  const t = useSafetyTranslation()
  const [pendingSignerAction, setPendingSignerAction] = useState<PendingSignerActionState | null>(
    null
  )
  const [pendingCompletionWarning, setPendingCompletionWarning] = useState<
    | { kind: 'add'; formData: AcknowledgmentSignerSelectionPayload }
    | { kind: 'promote'; item: AcknowledgmentCommitmentItem }
    | null
  >(null)
  const [isApplyingSigner, setIsApplyingSigner] = useState(false)
  const [pendingCommentAction, setPendingCommentAction] =
    useState<PendingCommentActionState | null>(null)
  const [pendingDeleteItem, setPendingDeleteItem] = useState<AcknowledgmentCommitmentItem | null>(
    null
  )
  const [planChangesItem, setPlanChangesItem] = useState<AcknowledgmentCommitmentItem | null>(null)
  const [planReviewItem, setPlanReviewItem] = useState<AcknowledgmentCommitmentItem | null>(null)
  const [pendingWarning, setPendingWarning] = useState<PendingSignatureState | null>(null)

  const {
    viewModel,
    partnerInfo,
    currentUserEmail,
    isDistributor,
    canManageSigners,
    canAddPbmByEmail,
    isLoading,
    error,
    isSubmitting,
    pendingItem,
    setPendingItem,
    handleSign,
    handleNotify,
    handleAddSigner,
    handleChangeSigner,
    handleSetOptional,
    handleDrop,
    handleRefuse,
    handleRemove,
    handleConfirmManager,
  } = useAcknowledgmentStatusDialog({
    partnerId,
    fYear,
    programType: offering,
    onSigned,
    onNotified,
    onUpdated,
  })

  const { canSimulate, simulate } = useAcknowledgmentSimulation()

  const runSignature = ({ mode, item }: PendingSignatureState) => {
    if (mode === 'sign') {
      void handleSign(item)
    } else {
      setPendingCommentAction({ mode: 'refuse', item })
    }
  }

  /* As on the partner page: the partner's side is warned first when the plan carries a warning. */
  const requestSignature = (signature: PendingSignatureState) => {
    if (signature.item.CommitmentType === CommitmentTypes.Partner && partnerInfo?.warningMessage) {
      setPendingWarning(signature)
    } else {
      runSignature(signature)
    }
  }

  /* From FY27 signing goes through the plan review first: weak areas are justified or fixed before
     the signature. */
  const finishPlanReview = (mode: PendingSignatureState['mode']) => {
    if (planReviewItem) {
      requestSignature({ mode, item: planReviewItem })
    }

    setPlanReviewItem(null)
  }

  const onSign = (item: AcknowledgmentCommitmentItem) => {
    if (isPlanReviewBeforeSign(fYear)) {
      setPlanReviewItem(item)
    } else {
      requestSignature({ mode: 'sign', item })
    }
  }

  const onAddPartner = (commitmentType: CommitmentTypes) => {
    setPendingSignerAction({
      commitmentType,
      intent: 'add',
    })
  }

  /* The Manager form takes any HP user by email; the signer is still added as a PBM. */
  const onAddPbmByEmail = (typeName: string) => {
    setPendingSignerAction({
      commitmentType: CommitmentTypes.PBM,
      intent: 'add',
      byEmail: { typeName },
    })
  }

  const onRequestChangeSigner = (item: AcknowledgmentCommitmentItem) => {
    setPendingSignerAction({
      commitmentType: item.CommitmentType,
      intent: item.CanChange !== 0 ? 'change' : 'select',
      item,
    })
  }

  const onApplySignerAction = async (
    formData: AcknowledgmentSignerSelectionPayload,
    confirmed = false
  ) => {
    if (!pendingSignerAction) {
      return
    }

    if (
      !confirmed &&
      pendingSignerAction.intent === 'add' &&
      !formData.isOptional &&
      isAcknowledgmentGroupComplete(viewModel.items, pendingSignerAction.commitmentType)
    ) {
      setPendingCompletionWarning({ kind: 'add', formData })
      return
    }

    setIsApplyingSigner(true)

    try {
      const isAddAction = pendingSignerAction.intent === 'add'
      const wasSubmitted = isAddAction
        ? await handleAddSigner(pendingSignerAction.commitmentType, formData)
        : pendingSignerAction.item
          ? await handleChangeSigner(
              pendingSignerAction.item,
              formData,
              pendingSignerAction.intent === 'change'
                ? AcknowledgmentAction.Change
                : AcknowledgmentAction.Select
            )
          : false

      if (wasSubmitted) {
        setPendingSignerAction(null)
      }
    } finally {
      setIsApplyingSigner(false)
    }
  }

  /* The add procedure skips an email already signing for the type, so the picker does not offer it. */
  const pendingSignerEmails = viewModel.items
    .filter((item) => item.CommitmentType === pendingSignerAction?.commitmentType)
    .map((item) => item.SignerEmail)
    .filter((email): email is string => Boolean(email))

  const pbmByEmail = pendingSignerAction?.byEmail

  const blockedReason = getBlockedSignOffReason(viewModel.items, currentUserEmail)

  const pendingSignerName =
    pendingItem?.SignerName ||
    pendingItem?.SignerEmail ||
    getTranslatedText({
      t,
      key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.signerFallback,
    })

  return (
    <div className={cn(styles.content, className)}>
      <LoaderOverlay isVisible={isSubmitting || isLoading} color="white" />

      {showPartnerSummary && (
        <PartnerSummaryBlock {...partnerSummary} className={styles.partnerSummary} />
      )}

      {/* A plan the server holds says why in place of the signer it waits for: nobody can sign
      or be reminded until then. */}
      {blockedReason ? (
        <AcknowledgmentBlockedBanner reason={blockedReason} action={blockedAction} />
      ) : (
        <AcknowledgmentPrimaryBanner
          primaryAction={viewModel.primaryAction}
          isSubmitting={isSubmitting}
          onRequestNotify={setPendingItem}
        />
      )}

      {error ? (
        <div className={styles.listArea}>
          <div className={styles.emptyState}>
            <Translation text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.loadError} />
          </div>
        </div>
      ) : viewModel.items.length === 0 ? (
        <div className={styles.listArea}>
          <div className={styles.emptyState}>
            <Translation text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.empty} />
          </div>
        </div>
      ) : (
        <div className={styles.columnsArea}>
          {viewModel.summaries.map((summary) => (
            <AcknowledgmentTypeColumn
              key={summary.typeName}
              anchorId={anchorColumns ? summary.commitmentType : undefined}
              summary={summary}
              items={viewModel.itemsByType[summary.typeName] ?? []}
              currentUserEmail={currentUserEmail}
              canManageSigners={canManageSigners}
              canAddPbmByEmail={canAddPbmByEmail}
              isSubmitting={isSubmitting}
              onSign={onSign}
              onRequestNotify={setPendingItem}
              onAddPartner={onAddPartner}
              onAddPbmByEmail={onAddPbmByEmail}
              onRequestRefuse={(item) => requestSignature({ mode: 'refuse', item })}
              onRequestDrop={(item) => setPendingCommentAction({ mode: 'drop', item })}
              onRequestChangeSigner={onRequestChangeSigner}
              onRequestRemove={(item) => setPendingDeleteItem(item)}
              onSetOptional={(item, isOptional) => {
                if (
                  !isOptional &&
                  item.IsOptional &&
                  item.IsAcknowledged !== 1 &&
                  isAcknowledgmentGroupComplete(viewModel.items, item.CommitmentType)
                ) {
                  setPendingCompletionWarning({ kind: 'promote', item })
                  return
                }
                void handleSetOptional(item, isOptional)
              }}
              onOpenPlanChanges={(item) => setPlanChangesItem(item)}
              onConfirmManager={(item) => void handleConfirmManager(item)}
              onSimulate={canSimulate ? simulate : undefined}
            />
          ))}
        </div>
      )}

      <AcknowledgmentFooter
        signedCount={viewModel.signedCount}
        totalCount={viewModel.totalCount}
        isSubmitting={isSubmitting}
        onClose={onClose}
      />

      <ConfirmDialog
        isVisible={pendingItem !== null}
        size="xs"
        title={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.resendNotificationTitle}
        content={
          pendingItem ? (
            <Translation
              span
              text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.resendNotificationContent}
              replaceParams={[{ key: '{signer}', value: pendingSignerName }]}
            />
          ) : null
        }
        confirmBtnText={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.notify}
        rejectBtnText="partnercommitment-cancel"
        isApplyDisabled={isSubmitting}
        isApplyLoading={isSubmitting}
        onConfirm={() => {
          if (pendingItem) {
            void handleNotify(pendingItem)
          }
        }}
        onReject={() => setPendingItem(null)}
      />

      <AcknowledgmentSignerActionDialog
        show={pendingSignerAction !== null}
        commitmentType={pendingSignerAction?.commitmentType ?? null}
        intent={pendingSignerAction?.intent ?? 'add'}
        mode={pbmByEmail ? 'manager' : undefined}
        title={
          pbmByEmail ? (
            <Translation
              text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.addPbmByEmailTitle}
              replaceParams={[{ key: '{typeName}', value: pbmByEmail.typeName }]}
            />
          ) : undefined
        }
        partnerId={partnerId}
        item={pendingSignerAction?.item}
        canAddOptional={viewModel.items.some(
          (item) =>
            item.CommitmentType === pendingSignerAction?.commitmentType &&
            Boolean(item.SignerEmail?.trim()) &&
            !item.IsOptional
        )}
        excludedEmails={pendingSignerEmails}
        isSubmitting={isApplyingSigner || isSubmitting}
        onClose={() => setPendingSignerAction(null)}
        onApply={onApplySignerAction}
      />

      <ConfirmDialog
        isVisible={pendingCompletionWarning !== null}
        size="xs"
        title={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.reopenGroupTitle}
        content={
          <Translation
            text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.reopenGroupContent}
          />
        }
        confirmBtnText={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.dialog.reopenGroupConfirm}
        rejectBtnText="partnercommitment-cancel"
        isApplyDisabled={isSubmitting || isApplyingSigner}
        isApplyLoading={isSubmitting || isApplyingSigner}
        onReject={() => setPendingCompletionWarning(null)}
        onConfirm={() => {
          const warning = pendingCompletionWarning
          if (!warning || isSubmitting || isApplyingSigner) return
          setPendingCompletionWarning(null)
          if (warning.kind === 'add') {
            void onApplySignerAction(warning.formData, true)
          } else {
            void handleSetOptional(warning.item, false)
          }
        }}
      />

      <AcknowledgmentCommentDialog
        show={pendingCommentAction !== null}
        mode={pendingCommentAction?.mode ?? 'drop'}
        isSubmitting={isSubmitting}
        onClose={() => setPendingCommentAction(null)}
        onApply={async (comment) => {
          if (!pendingCommentAction) {
            return
          }

          const wasSubmitted =
            pendingCommentAction.mode === 'drop'
              ? await handleDrop(pendingCommentAction.item, comment)
              : await handleRefuse(pendingCommentAction.item, comment)

          if (wasSubmitted) {
            setPendingCommentAction(null)
          }
        }}
      />

      {/* The confirmation closes at once: the panel's loader shows the removal, so the dialog is
      never left standing with its buttons disabled. */}
      <AcknowledgmentDeleteDialog
        show={pendingDeleteItem !== null}
        onClose={() => setPendingDeleteItem(null)}
        onConfirm={() => {
          if (pendingDeleteItem) {
            setPendingDeleteItem(null)
            void handleRemove(pendingDeleteItem)
          }
        }}
      />

      <PlanReviewDialog
        show={planReviewItem !== null && partnerInfo !== null}
        partnerId={partnerId}
        fYear={fYear}
        programType={offering as ProgramTypes | null}
        isGlobal={partnerInfo?.isGlobal}
        partner={{
          name: partnerInfo?.partnerName || partnerSummary?.partnerName,
          isDistributor: partnerInfo?.isDistributor,
        }}
        role={planReviewItem?.CommitmentType ?? CommitmentTypes.PBM}
        continueCaption={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.sign}
        showSummary
        onClose={() => setPlanReviewItem(null)}
        onContinue={() => finishPlanReview('sign')}
        onReject={() => finishPlanReview('refuse')}
      />

      <ConfirmDialog
        isVisible={pendingWarning !== null}
        size="xs"
        title="jbp-commitment-warning-header"
        content={<Translation span text={partnerInfo?.warningMessage ?? ''} />}
        confirmBtnText="jbp-continue"
        rejectBtnText="partnercommitment-cancel"
        onConfirm={() => {
          if (pendingWarning) {
            runSignature(pendingWarning)
          }

          setPendingWarning(null)
        }}
        onReject={() => setPendingWarning(null)}
      />

      <AcknowledgmentPlanChangesDialog
        show={planChangesItem !== null}
        partnerId={partnerId}
        fYear={fYear}
        programType={offering}
        changeDate={planChangesItem?.ActionDate}
        isDistributor={isDistributor}
        onClose={() => setPlanChangesItem(null)}
      />
    </div>
  )
}
